
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import Select from "react-select";
import Swal from "sweetalert2";
import Collapse from "react-bootstrap/Collapse";

import CashVoucherServices from "../../../services/CashVoucherServices";
import { downloadCashVoucherPDF } from "./CashVoucherPDF";

import Loader from "../Loader/loader";
import Pagination from "../Pagenation/Pagenation";

// ==========================================
// HELPERS
// ==========================================

const getId = (value) =>
    String(value?._id || value || "");

const getName = (value, fallback = "-") => {
    if (!value) return fallback;

    if (typeof value === "string") {
        return value;
    }

    return (
        value.name ||
        value.location ||
        value.description ||
        fallback
    );
};

const getError = (error) =>
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong. Please try again.";

const unwrap = (response) =>
    response?.data?.data ||
    response?.data ||
    response ||
    {};

const getArray = (response, keys) => {
    const data = unwrap(response);

    if (Array.isArray(data)) {
        return data;
    }

    for (const key of keys) {
        if (Array.isArray(data?.[key])) {
            return data[key];
        }
    }

    return [];
};

const money = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;

const formatVoucherDate = (value) => {
    if (!value) return "-";

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-");
        return `${day}/${month}/${year}`;
    }

    return value;
};

const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

// ==========================================
// PAGINATION CONFIGURATION
// ==========================================

const DEFAULT_PAGE_SIZE = 10;

const PAGE_OPTIONS = [5, 10, 20, 50, 100].map(
    (value) => ({
        value,
        label: String(value)
    })
);

const EMPTY_PAGINATION = {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    totalRecords: 0,
    totalPages: 0,
    totalAmount: 0
};

// ==========================================
// SECTION TOGGLE
// ==========================================

const SectionToggle = ({
    isOpen,
    onToggle,
    label
}) => (
    <button
        type="button"
        className="SlideToolHeader"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={label}
        title={
            isOpen
                ? "Collapse section"
                : "Expand section"
        }
        style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "32px",
            minHeight: "32px",
            padding: "4px",
            visibility: "visible",
            opacity: 1
        }}
    >
        <i
            className={`fas ${isOpen ? "fa-angle-up" : "fa-angle-down"
                }`}
            style={{
                fontSize: "20px",
                color: "#b6bcc5",
                pointerEvents: "none"
            }}
        />
    </button>
);

// ==========================================
// GENERATED VOUCHERS
// ==========================================

const GeneratedVouchers = () => {
    // ========================================
    // STATES
    // ========================================

    const [stores, setStores] = useState([]);
    const [passbooks, setPassbooks] = useState([]);

    const [selectedStore, setSelectedStore] =
        useState(null);

    const [selectedPassbook, setSelectedPassbook] =
        useState(null);

    const [loadingStores, setLoadingStores] =
        useState(false);

    const [loadingPassbooks, setLoadingPassbooks] =
        useState(false);

    const [searching, setSearching] =
        useState(false);

    const [vouchers, setVouchers] =
        useState([]);

    const [hasSearched, setHasSearched] =
        useState(false);

    const [searchText, setSearchText] =
        useState("");

    // Filter closed by default
    const [isFilterOpen, setIsFilterOpen] =
        useState(false);

    // Voucher results open by default
    const [
        isVoucherSectionOpen,
        setIsVoucherSectionOpen
    ] = useState(true);

    const [currentPage, setCurrentPage] =
        useState(1);

    const [pageSize, setPageSize] =
        useState(DEFAULT_PAGE_SIZE);

    const [pagination, setPagination] =
        useState({ ...EMPTY_PAGINATION });

    const [
        pdfDownloadingId,
        setPdfDownloadingId
    ] = useState(null);

    const passbookRequestRef = useRef(0);
    const searchRequestRef = useRef(0);

    // ========================================
    // REACT SELECT STYLES
    // ========================================

    const selectStyles = useMemo(
        () => ({
            control: (base) => ({
                ...base,
                minHeight: "48px",
                height: "48px",
                borderRadius: "12px"
            }),

            valueContainer: (base) => ({
                ...base,
                height: "46px",
                padding: "0 12px"
            }),

            indicatorsContainer: (base) => ({
                ...base,
                height: "46px"
            })
        }),
        []
    );

    // ========================================
    // LOAD STORES
    // ========================================

    useEffect(() => {
        let active = true;

        const loadStores = async () => {
            setLoadingStores(true);

            try {
                const response =
                    await CashVoucherServices.getStores();

                if (!active) return;

                setStores(
                    getArray(response, [
                        "stores",
                        "locations"
                    ])
                );
            } catch (error) {
                if (active) {
                    Swal.fire(
                        "Error",
                        getError(error),
                        "error"
                    );
                }
            } finally {
                if (active) {
                    setLoadingStores(false);
                }
            }
        };

        loadStores();

        return () => {
            active = false;
            passbookRequestRef.current += 1;
            searchRequestRef.current += 1;
        };
    }, []);

    // ========================================
    // STORE OPTIONS
    // ========================================

    const storeOptions = useMemo(
        () =>
            stores.map((store) => ({
                value: getId(store),
                label:
                    getName(store) +
                    (store.location
                        ? ` - ${store.location}`
                        : "")
            })),
        [stores]
    );

    // ========================================
    // PASSBOOK OPTIONS
    // ========================================

    const passbookOptions = useMemo(
        () =>
            passbooks.map((passbook) => ({
                value: getId(passbook),
                label: getName(passbook)
            })),
        [passbooks]
    );

    // ========================================
    // FILTER CURRENT PAGE VOUCHERS
    // ========================================

    const filteredVouchers = useMemo(() => {
        const keyword = searchText
            .trim()
            .toLowerCase();

        if (!keyword) {
            return vouchers;
        }

        return vouchers.filter((voucher) => {
            const searchableText = [
                voucher.voucherNumber,
                voucher.voucherDate,
                formatVoucherDate(voucher.voucherDate),
                getName(voucher.store),
                getName(voucher.passbook),
                voucher.totalEntries,
                voucher.totalAmount,
                voucher.revision,
                voucher.generatedAt,
                formatDateTime(
                    voucher.generatedAt ||
                    voucher.createdAt
                ),
                voucher.generatedBy?.name,
                voucher.generatedBy?.fullName,
                voucher.generatedBy?.username
            ]
                .map((value) => String(value ?? ""))
                .join(" ")
                .toLowerCase();

            return searchableText.includes(keyword);
        });
    }, [vouchers, searchText]);

    // ========================================
    // RESET RESULTS
    // ========================================

    const resetResults = () => {
        searchRequestRef.current += 1;

        setVouchers([]);
        setHasSearched(false);
        setSearching(false);
        setCurrentPage(1);
        setSearchText("");

        setPagination({
            ...EMPTY_PAGINATION,
            limit: pageSize
        });
    };

    // ========================================
    // STORE CHANGE
    // ========================================

    const handleStoreChange = async (option) => {
        const requestId =
            ++passbookRequestRef.current;

        setSelectedStore(option);
        setSelectedPassbook(null);
        setPassbooks([]);

        resetResults();

        if (!option) {
            setLoadingPassbooks(false);
            return;
        }

        setLoadingPassbooks(true);

        try {
            const response =
                await CashVoucherServices.getPassbooks(
                    option.value
                );

            if (
                requestId !== passbookRequestRef.current
            ) {
                return;
            }

            setPassbooks(
                getArray(response, [
                    "passbooks",
                    "cashPassbooks"
                ])
            );
        } catch (error) {
            if (
                requestId === passbookRequestRef.current
            ) {
                Swal.fire(
                    "Error",
                    getError(error),
                    "error"
                );
            }
        } finally {
            if (
                requestId === passbookRequestRef.current
            ) {
                setLoadingPassbooks(false);
            }
        }
    };

    // ========================================
    // PASSBOOK CHANGE
    // ========================================

    const handlePassbookChange = (option) => {
        setSelectedPassbook(option);
        resetResults();
    };

    // ========================================
    // SEARCH VOUCHERS
    // ========================================

    const handleSearch = async (
        page = 1,
        limit = pageSize
    ) => {
        if (!selectedStore) {
            return Swal.fire(
                "Select Store",
                "Please select Store / Location.",
                "warning"
            );
        }

        if (!selectedPassbook) {
            return Swal.fire(
                "Select CashBook",
                "Please select CashBook / Passbook.",
                "warning"
            );
        }

        const requestId =
            ++searchRequestRef.current;

        setSearching(true);

        try {
            const response =
                await CashVoucherServices.getVouchers({
                    storeId: selectedStore.value,
                    passbookId: selectedPassbook.value,
                    page,
                    limit
                });

            // Ignore outdated API responses
            if (
                requestId !== searchRequestRef.current
            ) {
                return;
            }

            const data = unwrap(response);

            const records = getArray(response, [
                "vouchers",
                "cashVouchers"
            ]);

            // Match selected Store and CashBook
            const filteredRecords = records.filter(
                (voucher) =>
                    getId(voucher.passbook) ===
                    selectedPassbook.value &&
                    getId(voucher.store) ===
                    selectedStore.value
            );

            const pageInfo = data.pagination;

            if (!pageInfo) {
                throw new Error(
                    "Pagination data is missing. Please update the getVouchers backend API."
                );
            }

            // Current page amount
            const currentPageAmount =
                filteredRecords.reduce(
                    (sum, voucher) =>
                        sum + Number(voucher.totalAmount || 0),
                    0
                );

            const backendTotalAmount = Number(
                pageInfo.totalAmount
            );

            const totalRecords = Number(
                pageInfo.totalRecords ?? 0
            );

            // Grand total
            let finalTotalAmount = 0;

            if (
                Number.isFinite(backendTotalAmount) &&
                backendTotalAmount > 0
            ) {
                finalTotalAmount = backendTotalAmount;
            } else if (
                totalRecords === filteredRecords.length
            ) {
                // Fallback when all records fit on one page
                finalTotalAmount = currentPageAmount;
            }

            // Multiple pages require a correct
            // totalAmount from the backend API.

            const nextPagination = {
                page: Number(
                    pageInfo.page ?? page
                ),

                limit: Number(
                    pageInfo.limit ?? limit
                ),

                totalRecords,

                totalPages: Number(
                    pageInfo.totalPages ?? 0
                ),

                totalAmount: finalTotalAmount
            };

            setVouchers(filteredRecords);
            setPagination(nextPagination);
            setCurrentPage(nextPagination.page);
            setPageSize(nextPagination.limit);
            setHasSearched(true);

        } catch (error) {
            if (
                requestId === searchRequestRef.current
            ) {
                Swal.fire(
                    "Unable to Search Vouchers",
                    getError(error),
                    "error"
                );
            }
        } finally {
            if (
                requestId === searchRequestRef.current
            ) {
                setSearching(false);
            }
        }
    };

    // ========================================
    // PAGE CHANGE
    // ========================================

    const handlePageChange = (zeroBasedPage) => {
        const page = Number(zeroBasedPage) + 1;

        if (
            searching ||
            !Number.isInteger(page) ||
            page < 1 ||
            page > pagination.totalPages ||
            page === currentPage
        ) {
            return;
        }

        handleSearch(page, pageSize);
    };

    // ========================================
    // ROWS PER PAGE CHANGE
    // ========================================

    const handlePageSizeChange = (option) => {
        if (!option || searching) {
            return;
        }

        const newLimit = Number(option.value);

        if (
            ![5, 10, 20, 50, 100].includes(newLimit)
        ) {
            return;
        }

        setPageSize(newLimit);
        setCurrentPage(1);

        if (
            hasSearched &&
            selectedStore &&
            selectedPassbook
        ) {
            handleSearch(1, newLimit);
        } else {
            setPagination((prev) => ({
                ...prev,
                page: 1,
                limit: newLimit
            }));
        }
    };

    // ========================================
    // DOWNLOAD VOUCHER PDF
    // ========================================

    const handleDownloadVoucher = async (
        voucherId
    ) => {
        if (!voucherId || pdfDownloadingId) {
            return;
        }

        setPdfDownloadingId(voucherId);

        try {
            await downloadCashVoucherPDF(voucherId);
        } catch (error) {
            Swal.fire(
                "PDF Generation Failed",
                getError(error),
                "error"
            );
        } finally {
            setPdfDownloadingId(null);
        }
    };

    // ========================================
    // PAGINATION COUNTS
    // ========================================

    const startRecord =
        pagination.totalRecords === 0
            ? 0
            : (currentPage - 1) * pageSize + 1;

    const endRecord = Math.min(
        currentPage * pageSize,
        pagination.totalRecords
    );

    const isBusy =
        loadingStores ||
        loadingPassbooks ||
        searching;

    // ========================================
    // RENDER
    // ========================================

    return (
        <div className="row">
            <div className="col-xl-12">

                {/* =====================================
            SECTION 1 - FILTER
            CLOSED BY DEFAULT
        ===================================== */}

                <div className="filter cm-content-box box-primary">

                    <div className="content-title">
                        <div className="cpa">
                            <i className="fas fa-filter me-2" />
                            Filter
                        </div>

                        <div className="tools">
                            <SectionToggle
                                isOpen={isFilterOpen}
                                onToggle={() =>
                                    setIsFilterOpen((prev) => !prev)
                                }
                                label="Toggle filter section"
                            />
                        </div>
                    </div>

                    <Collapse in={isFilterOpen}>
                        <div className="cm-content-body form excerpt">
                            <div className="card-body">

                                <div className="row g-3">

                                    {/* SEARCH TEXT */}
                                    <div className="col-12 col-md-6">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by voucher no., date, store, CashBook..."
                                            value={searchText}
                                            onChange={(event) =>
                                                setSearchText(
                                                    event.target.value
                                                )
                                            }
                                            style={{
                                                height: "48px",
                                                borderRadius: "12px"
                                            }}
                                        />
                                    </div>

                                    {/* ROWS PER PAGE */}
                                    <div className="col-12 col-md-6">
                                        <Select
                                            options={PAGE_OPTIONS}
                                            value={
                                                PAGE_OPTIONS.find(
                                                    (option) =>
                                                        option.value === pageSize
                                                ) || null
                                            }
                                            onChange={handlePageSizeChange}
                                            isSearchable={false}
                                            isDisabled={searching}
                                            placeholder="Select rows to display in Voucher Table"
                                            className="custom-react-select"
                                            styles={selectStyles}
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    </Collapse>
                </div>

                {/* =====================================
            SECTION 2 - GENERATED VOUCHERS
            OPEN BY DEFAULT
        ===================================== */}

                <div className="filter cm-content-box box-primary">

                    <div className="content-title">
                        <div className="cpa">
                            <i className="fa fa-list me-2" />
                            Generated Voucher Results

                            <span className="badge bg-orange ms-2">
                                {pagination.totalRecords}
                            </span>
                        </div>

                        <div className="tools">
                            <SectionToggle
                                isOpen={isVoucherSectionOpen}
                                onToggle={() =>
                                    setIsVoucherSectionOpen(
                                        (prev) => !prev
                                    )
                                }
                                label="Toggle generated voucher results"
                            />
                        </div>
                    </div>

                    <Collapse in={isVoucherSectionOpen}>
                        <div className="cm-content-body form excerpt">
                            <div className="card-body">

                                {/* =================================
                    STORE / CASHBOOK / SEARCH
                ================================= */}

                                <div className="row g-3 align-items-end mb-4">

                                    {/* STORE */}
                                    <div className="col-12 col-md-5">
                                        <label className="form-label">
                                            Store / Location
                                        </label>

                                        <Select
                                            options={storeOptions}
                                            styles={selectStyles}
                                            value={selectedStore}
                                            onChange={handleStoreChange}
                                            isClearable
                                            isLoading={loadingStores}
                                            isDisabled={searching}
                                            placeholder="Select Store / Location"
                                        />
                                    </div>

                                    {/* CASHBOOK */}
                                    <div className="col-12 col-md-5">
                                        <label className="form-label">
                                            CashBook / Passbook
                                        </label>

                                        <Select
                                            options={passbookOptions}
                                            styles={selectStyles}
                                            value={selectedPassbook}
                                            onChange={handlePassbookChange}
                                            isClearable
                                            isLoading={loadingPassbooks}
                                            isDisabled={
                                                !selectedStore ||
                                                loadingPassbooks ||
                                                searching
                                            }
                                            placeholder="Select CashBook / Passbook"
                                        />
                                    </div>

                                    {/* SEARCH BUTTON */}
                                    <div className="col-12 col-md-2">
                                        <button
                                            type="button"
                                            className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                                            style={{
                                                height: "48px",
                                                borderRadius: "8px",
                                                fontSize: "14px",
                                                fontWeight: 600
                                            }}
                                            disabled={
                                                isBusy ||
                                                !selectedStore ||
                                                !selectedPassbook
                                            }
                                            onClick={() =>
                                                handleSearch(1, pageSize)
                                            }
                                        >
                                            {searching ? (
                                                <>
                                                    <i className="fa fa-spinner fa-spin me-2" />
                                                    Searching...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-search me-2" />
                                                    Search
                                                </>
                                            )}
                                        </button>
                                    </div>

                                </div>

                                {/* =================================
                    RESULTS AFTER SEARCH
                ================================= */}

                                {(hasSearched || searching) && (
                                    <>

                                        {/* =============================
                        SUMMARY CARDS
                    ============================= */}

                                        <div className="row g-3 mb-4">

                                            {/* TOTAL GENERATED VOUCHERS */}
                                            <div className="col-12 col-md-6">
                                                <div
                                                    style={{
                                                        background:
                                                            "linear-gradient(120deg, #e5f0ff 0%, #d3e5ff 55%, #c1dbff 100%)",
                                                        border: "1px solid #b7d4fa",
                                                        borderRadius: "16px",
                                                        padding: "22px 26px",
                                                        minHeight: "120px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        boxShadow:
                                                            "0 4px 14px rgba(35, 100, 190, 0.07)"
                                                    }}
                                                >
                                                    <div>
                                                        <div
                                                            style={{
                                                                fontSize: "14px",
                                                                fontWeight: 600,
                                                                color: "#42658e",
                                                                marginBottom: "10px"
                                                            }}
                                                        >
                                                            Total Generated Vouchers
                                                        </div>

                                                        <div
                                                            style={{
                                                                fontSize: "30px",
                                                                fontWeight: 700,
                                                                color: "#164b91",
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {pagination.totalRecords}
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            width: "56px",
                                                            height: "56px",
                                                            borderRadius: "16px",
                                                            background:
                                                                "rgba(47, 116, 213, 0.12)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}
                                                    >
                                                        <i
                                                            className="fa fa-file-text-o"
                                                            style={{
                                                                fontSize: "26px",
                                                                color: "#3276c7"
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* TOTAL VOUCHER AMOUNT */}
                                            <div className="col-12 col-md-6">
                                                <div
                                                    style={{
                                                        background:
                                                            "linear-gradient(120deg, #e5f8ee 0%, #d2f2e1 55%, #bcebd1 100%)",
                                                        border: "1px solid #a9dfc3",
                                                        borderRadius: "16px",
                                                        padding: "22px 26px",
                                                        minHeight: "120px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        boxShadow:
                                                            "0 4px 14px rgba(30, 140, 90, 0.07)"
                                                    }}
                                                >
                                                    <div>
                                                        <div
                                                            style={{
                                                                fontSize: "14px",
                                                                fontWeight: 600,
                                                                color: "#38775b",
                                                                marginBottom: "10px"
                                                            }}
                                                        >
                                                            Total Voucher Amount
                                                        </div>

                                                        <div
                                                            style={{
                                                                fontSize: "30px",
                                                                fontWeight: 700,
                                                                color: "#12663e",
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {money(pagination.totalAmount)}
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            width: "56px",
                                                            height: "56px",
                                                            borderRadius: "16px",
                                                            background:
                                                                "rgba(32, 151, 91, 0.12)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}
                                                    >
                                                        <i
                                                            className="fa fa-inr"
                                                            style={{
                                                                fontSize: "27px",
                                                                color: "#22955a"
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                        </div>

                                        {/* =================================
                        LOADER OR VOUCHER TABLE
                    ================================= */}

                                        {searching ? (
                                            <Loader />
                                        ) : (
                                            <>

                                                <div className="table-responsive">
                                                    <div
                                                        id="content_wrapper"
                                                        className="dataTables_wrapper no-footer"
                                                    >
                                                        <div
                                                            style={{
                                                                overflowX: "auto",
                                                                overflowY: "auto",
                                                                maxHeight: "400px",
                                                                whiteSpace: "nowrap",
                                                                position: "relative",
                                                                zIndex: 0
                                                            }}
                                                        >
                                                            <table
                                                                className="table table-bordered table-responsive-lg table-striped table-condensed flip-content"
                                                                id="generated-vouchers-data"
                                                            >

                                                                {/* TABLE HEADER */}
                                                                <thead
                                                                    style={{
                                                                        position: "sticky",
                                                                        top: 0,
                                                                        backgroundColor: "#fff",
                                                                        zIndex: 1
                                                                    }}
                                                                >
                                                                    <tr>
                                                                        <th>S.No</th>
                                                                        <th>Voucher No.</th>
                                                                        <th>Voucher For Date</th>
                                                                        <th>CashBook Details</th>
                                                                        <th>Amount</th>
                                                                        <th>Generated Details</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>

                                                                {/* TABLE BODY */}
                                                                <tbody>
                                                                    {filteredVouchers.length > 0 ? (
                                                                        filteredVouchers.map(
                                                                            (voucher, index) => {
                                                                                const voucherId =
                                                                                    getId(voucher);

                                                                                const generatedBy =
                                                                                    voucher.generatedBy &&
                                                                                        typeof voucher.generatedBy === "object"
                                                                                        ? voucher.generatedBy.name ||
                                                                                        voucher.generatedBy.fullName ||
                                                                                        voucher.generatedBy.username ||
                                                                                        "-"
                                                                                        : "-";

                                                                                return (
                                                                                    <tr key={voucherId}>

                                                                                        {/* SERIAL NUMBER */}
                                                                                        <td>
                                                                                            {(currentPage - 1) *
                                                                                                pageSize +
                                                                                                index +
                                                                                                1}
                                                                                        </td>

                                                                                        {/* VOUCHER NUMBER */}
                                                                                        <td>
                                                                                            {voucher.voucherNumber || "-"}
                                                                                        </td>

                                                                                        {/* VOUCHER DATE */}
                                                                                        <td>
                                                                                            {formatVoucherDate(
                                                                                                voucher.voucherDate
                                                                                            )}
                                                                                        </td>

                                                                                        <td>
                                                                                            <div>{getName(voucher.store)}</div>
                                                                                            <div>{getName(voucher.passbook)}</div>
                                                                                            <div>Entries: {voucher.totalEntries || 0}</div>
                                                                                        </td>

                                                                                        {/* AMOUNT */}
                                                                                        <td>
                                                                                            {money(
                                                                                                voucher.totalAmount
                                                                                            )}
                                                                                        </td>
                                                                                        {/* GENERATED DETAILS */}
                                                                                        <td>
                                                                                            <div className="d-flex flex-column gap-1">

                                                                                                {/* DATE AND TIME */}
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <i
                                                                                                        className="fa fa-calendar-o me-2"
                                                                                                        style={{
                                                                                                            color: "#6c757d"
                                                                                                        }}
                                                                                                    />

                                                                                                    <span>
                                                                                                        {formatDateTime(
                                                                                                            voucher.generatedAt ||
                                                                                                            voucher.createdAt
                                                                                                        )}
                                                                                                    </span>
                                                                                                </div>

                                                                                                {/* GENERATED BY */}
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <i
                                                                                                        className="fa fa-user-o me-2"
                                                                                                        style={{
                                                                                                            color: "#6c757d"
                                                                                                        }}
                                                                                                    />

                                                                                                    <span>
                                                                                                        {generatedBy}
                                                                                                    </span>
                                                                                                </div>

                                                                                            </div>
                                                                                        </td>


                                                                                        {/* ACTION - PDF DOWNLOAD */}
                                                                                        <td className="text-center align-middle">
                                                                                            <button
                                                                                                type="button"
                                                                                                title="Download Voucher PDF"
                                                                                                aria-label="Download Voucher PDF"
                                                                                                disabled={
                                                                                                    searching ||
                                                                                                    Boolean(pdfDownloadingId)
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    handleDownloadVoucher(voucherId)
                                                                                                }
                                                                                                style={{
                                                                                                    display: "inline-flex",
                                                                                                    alignItems: "center",
                                                                                                    justifyContent: "center",
                                                                                                    background: "transparent",
                                                                                                    border: "none",
                                                                                                    padding: "6px 10px",
                                                                                                    color: "#dc3545",
                                                                                                    cursor:
                                                                                                        searching || pdfDownloadingId
                                                                                                            ? "not-allowed"
                                                                                                            : "pointer"
                                                                                                }}
                                                                                            >
                                                                                                {pdfDownloadingId === voucherId ? (
                                                                                                    <span
                                                                                                        className="spinner-border spinner-border-sm"
                                                                                                        role="status"
                                                                                                        aria-label="Generating PDF"
                                                                                                    />
                                                                                                ) : (
                                                                                                    <svg
                                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                                        width="27"
                                                                                                        height="27"
                                                                                                        viewBox="0 0 24 24"
                                                                                                        fill="none"
                                                                                                        stroke="#dc3545"
                                                                                                        strokeWidth="1.8"
                                                                                                        strokeLinecap="round"
                                                                                                        strokeLinejoin="round"
                                                                                                        aria-hidden="true"
                                                                                                        style={{
                                                                                                            display: "block",
                                                                                                            flexShrink: 0
                                                                                                        }}
                                                                                                    >
                                                                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                                                        <polyline points="14 2 14 8 20 8" />
                                                                                                        <path d="M8 13h2a1.5 1.5 0 0 1 0 3H8v-3z" />
                                                                                                        <path d="M8 16v2" />
                                                                                                        <path d="M13 13v5" />
                                                                                                        <path d="M13 13h1a2 2 0 0 1 0 4h-1" />
                                                                                                        <path d="M18 13h-2v5" />
                                                                                                        <path d="M16 15.5h1.5" />
                                                                                                    </svg>
                                                                                                )}
                                                                                            </button>
                                                                                        </td>


                                                                                    </tr>
                                                                                );
                                                                            }
                                                                        )
                                                                    ) : (
                                                                        <tr>
                                                                            <td
                                                                                colSpan={7}
                                                                                className="text-center"
                                                                            >
                                                                                {searchText.trim()
                                                                                    ? "No matching vouchers found on this page."
                                                                                    : "No generated vouchers found for the selected CashBook."}
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>


                                                                {/* GRAND TOTAL */}
                                                                {vouchers.length > 0 && (
                                                                    <tfoot className="table-light">
                                                                        <tr>
                                                                            <th
                                                                                colSpan={4}
                                                                                className="text-end"
                                                                            >
                                                                                Grand Total
                                                                            </th>

                                                                            <th className="text-end">
                                                                                {money(pagination.totalAmount)}
                                                                            </th>

                                                                            <th colSpan={2} />
                                                                        </tr>
                                                                    </tfoot>
                                                                )}

                                                            </table>
                                                        </div>

                                                        {/* PAGINATION */}
                                                        {hasSearched && (
                                                            <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">

                                                                <div className="dataTables_info">
                                                                    {pagination.totalRecords > 0 ? (
                                                                        <>
                                                                            Showing {startRecord} to{" "}
                                                                            {endRecord} of{" "}
                                                                            {pagination.totalRecords} entries
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            Showing 0 to 0 of 0 entries
                                                                        </>
                                                                    )}
                                                                </div>

                                                                <Pagination
                                                                    activePage={currentPage - 1}
                                                                    totalPages={
                                                                        pagination.totalPages
                                                                    }
                                                                    onPageChange={
                                                                        handlePageChange
                                                                    }
                                                                />

                                                            </div>
                                                        )}

                                                    </div>
                                                </div>

                                            </>
                                        )}

                                    </>
                                )}

                            </div>
                        </div>
                    </Collapse>

                </div>

            </div>
        </div>
    );
};

export default GeneratedVouchers;
